import React, {useEffect, useRef, useState} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useOrganizationApi } from "../Services/OrganizationApiProvider";
import Breadcrumbs from "../Components/Breadcrumbs";
import { useChatAnalyticsApi } from "../Services/ChatAnalyticsApiProvider";
import PeriodSelector from "../Components/PeriodSelector";
import CustomDateRangePicker from "../Components/CustomDateRangePicker";
import Table from "../Components/Tables";
import AnalyticsExpandableWrapper from "../Components/AnalyticsExpandableRow";
import "../Components/TotalAnalytics.css";
import "../Components/OrganizationDetail.css";
import { useAuth } from "../Contexts/AuthContext";
import { AlertProvider, useAlert } from "../Components/AlertContext";

const getDateRangeByPeriod = (period) => {
  const today = new Date();
  let startDate = null;

  switch (period) {
    case "all_time":
      startDate = new Date(2000, 0, 1);
      break;
    case "year":
      startDate = new Date();
      startDate.setFullYear(today.getFullYear() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;
    case "month":
      startDate = new Date();
      startDate.setMonth(today.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;
    case "day":
      startDate = new Date();
      startDate.setDate(today.getDate());
      startDate.setHours(0, 0, 0, 0);
      break;
    default:
      startDate = null;
  }
  return {
    start_date: startDate ? startDate.toISOString() : null,
    end_date: today.toISOString()
  };
};

function OrganizationContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { status } = useAuth();
  const { organizationId } = useParams();
  const { getOrganizationById } = useOrganizationApi();
  const { getTotalChatAnalytics, getDetailedChatAnalytics, getTokenAnalytics } = useChatAnalyticsApi();

  const [organization, setOrganization] = useState();
  const [analytics, setAnalytics] = useState([]);
  const [detailedAnalytics, setDetailedAnalytics] = useState([]);
  const [tokenAnalytics, setTokenAnalytics] = useState([]);
  const hasOrganizationsPermission = status?.permissions?.includes("all:organizations");


  const alertShownRef = useRef(false);

  const [selectedPeriod, setSelectedPeriod] = useState("day");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [selectedDetailedPeriod, setSelectedDetailedPeriod] = useState("day");
  const [customDetailedFrom, setCustomDetailedFrom] = useState("");
  const [customDetailedTo, setCustomDetailedTo] = useState("");

  const [selectedTokenPeriod, setSelectedTokenPeriod] = useState("day");
  const [customTokenFrom, setCustomTokenFrom] = useState("");
  const [customTokenTo, setCustomTokenTo] = useState("");

  const [totalChannels, setTotalChannels] = useState(0);
  const [channelsPerPage, setChannelsPerPage] = useState(10);

  const columns = [
    { name: "ID", selector: row => row.channel_id },
    { name: "Channel Name", selector: row => row.channel_name },
    { name: "Total Chats", selector: row => row.total_chats, sortable: true },
    { name: "Active Chats", selector: row => row.active_chats, sortable: true },
    { name: "Inactive Chats", selector: row => row.inactive_chats, sortable: true }
  ];

  useEffect(() => {
    if (location.state?.successMessage && !alertShownRef.current) {
      showAlert("success", location.state.successMessage);
      alertShownRef.current = true;
      window.history.replaceState({}, document.title);
    }
  }, [location, showAlert]);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const shouldSkipCache = !!location.state?.successMessage;
        const org = await getOrganizationById(organizationId, shouldSkipCache);
        setOrganization(org);
      } catch (error) {
        console.error("Error fetching organization:", error);
        navigate("/404");
      }
    };
    fetchOrganization();
  }, [organizationId, getOrganizationById, navigate]);

  useEffect(() => {
    const fetchChatAnalytics = async () => {
      try {
        let start_date, end_date;

        if (selectedPeriod === "custom") {
          if (!customFrom || !customTo) return;
          start_date = customFrom;
          end_date = customTo;
        } else {
          ({ start_date, end_date } = getDateRangeByPeriod(selectedPeriod));
        }

        const analyticsData = await getTotalChatAnalytics(start_date, end_date);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error("Error fetching total analytics:", error);
      }
    };

    fetchChatAnalytics();
  }, [selectedPeriod, customFrom, customTo, getTotalChatAnalytics]);

  const fetchDetailedAnalytics = async (skip = 0, limit = channelsPerPage) => {
      if (limit !== channelsPerPage) {
        setChannelsPerPage(limit);
      }
      try {
        let start_date, end_date;

        if (selectedDetailedPeriod === "custom") {
          if (!customDetailedFrom || !customDetailedTo) return;
          start_date = customDetailedFrom;
          end_date = customDetailedTo;
        } else {
          ({ start_date, end_date } = getDateRangeByPeriod(selectedDetailedPeriod));
        }

      const response = await getDetailedChatAnalytics(start_date, end_date, skip, limit);
      setDetailedAnalytics(response.analyticsData);
      setTotalChannels(response.items);

      } catch (error) {
        console.error("Error fetching detailed analytics:", error);
      }
    };

  useEffect(() => {
    fetchDetailedAnalytics(0, channelsPerPage);
  }, [selectedDetailedPeriod, customDetailedFrom, customDetailedTo, getDetailedChatAnalytics]);

  const fetchTokenAnalytics = async () => {
    try {
      let start_date, end_date;

      if (selectedTokenPeriod === "custom") {
        if (!customTokenFrom || !customTokenTo) return;
        start_date = customTokenFrom;
        end_date = customTokenTo;
      } else {
        ({ start_date, end_date } = getDateRangeByPeriod(selectedTokenPeriod));
      }

      const analyticsData = await getTokenAnalytics(start_date, end_date);
      setTokenAnalytics(analyticsData);
    } catch (error) {
      console.error("Error fetching token analytics:", error);
    }
  };

  useEffect(() => {
    if (hasOrganizationsPermission) {
      fetchTokenAnalytics();
    }
  }, [selectedTokenPeriod, customTokenFrom, customTokenTo, getTokenAnalytics, status?.permissions]);



  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  const handleDetailedPeriodChange = (e) => setSelectedDetailedPeriod(e.target.value);

  const handleTokenPeriodChange = (e) => setSelectedTokenPeriod(e.target.value);

  if (!organization) return <div>Organization not found.</div>;
  if (!analytics) return <div>Loading analytics...</div>;

  return (
      <div className="container">
        <Breadcrumbs />
        <div className="row d-flex gap">
          <div className="radius-yes p-2 bg-yes col-12">
            <div className="org-details-header">
              <h2>Organization Details</h2>
                <button
                  className="edit-btn"
                  onClick={() => {
                    navigate(`/organizations/${organizationId}/detail`);
                  }}
                  title="Edit parameter"
                >
                  <i className="fa-solid fa-pencil"></i>
                </button>
          </div>

            <div className="org-header">
              <h2>{organization.title}</h2>
              <span className={`status-badge ${organization.is_active ? 'active' : 'inactive'}`}>
                {organization.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="detail-item">
              <label>ID:</label>
              <span>{organization.id}</span>
            </div>
            <div className="detail-item">
              <label>Description:</label>
              <p>{organization.description || "No description available."}</p>
            </div>
            <div className="detail-item">
              <label>Active Communications:</label>
              <p>{organization?.active_communications}</p>
            </div>
            <div className="detail-item">
              <label>Communications Per Day:</label>
              <p>{organization?.communications_per_day}</p>
            </div>
            <div className="detail-item">
              <label>Communications Per Month:</label>
              <p>{organization?.communications_per_month}</p>
            </div>
          </div>

          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="mb-4">Total Analytics</h2>
            <div className="analytics-grid">

              <div className="analytics-card">
                <div className="analytics-header">
                  <h3>Total Chats</h3>
                </div>
                <div className="analytics-value">{analytics.total_chats?.toLocaleString() || 0}</div>
                <div className="analytics-details">
                  <div className="detail-item">
                    <span className="label">Open chats</span>
                    <span className="value">{analytics.active_chats?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Closed chats</span>
                    <span className="value">{analytics.inactive_chats?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Used chats</span>
                    <span className="value">{analytics.used_chats?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Average total message per chat</span>
                    <span className="value">{analytics.total_message_per_thread?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Average AI message per chat</span>
                    <span className="value">{analytics.ai_message_per_thread?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <div className="analytics-header">
                  <h3>Total Messages</h3>
                </div>
                <div className="analytics-value">{analytics.total_messages?.toLocaleString() || 0}</div>
                <div className="analytics-details">
                  <div className="detail-item">
                    <span className="label">Incoming</span>
                    <span className="value">{analytics.input_messages?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Outgoing</span>
                    <span className="value">{analytics.output_messages?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
              <div className="analytics-card">
                <div className="analytics-header">
                  <h3>AI Statistics</h3>
                </div>
                <div className="analytics-details">
                  <div className="detail-item">
                    <span className="label">Generated messages</span>
                    <span className="value">{analytics.generated_messages?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Sent messages</span>
                    <span className="value">{analytics.is_sent?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Redacted messages</span>
                    <span className="value">{analytics.is_redacted?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {analytics.total_tokens != null && (
                  <div className="analytics-card">
                    <div className="analytics-header">
                      <h3>Total Tokens</h3>
                    </div>
                    <div className="analytics-value">{analytics.total_tokens?.toLocaleString() || 0}</div>
                    <div className="analytics-details">
                      <div className="detail-item">
                        <span className="label">Input tokens</span>
                        <span className="value">{analytics.input_tokens?.toLocaleString() || 0}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Output tokens</span>
                        <span className="value">{analytics.output_tokens?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  </div>
              )}
            </div>

            <div style={{marginTop: "6vh"}}>
              <PeriodSelector selectedPeriod={selectedPeriod} onChange={handlePeriodChange}/>
              {selectedPeriod === "custom" && (
                  <CustomDateRangePicker
                      customFrom={customFrom}
                      customTo={customTo}
                      setCustomFrom={setCustomFrom}
                      setCustomTo={setCustomTo}
                  />
              )}
            </div>
          </div>

          <div className="radius-yes p-2 bg-yes col-12">
            <h2>Channels</h2>
            {totalChannels === 0 ? (
                <p>No detailed data available.</p>
            ) : (
                <Table
                    key={selectedDetailedPeriod}
                    columns={columns}
                    data={detailedAnalytics}
                    selectableRowsHighlight
                    expandableRows
                    expandableRowsComponent={AnalyticsExpandableWrapper}
                    paginationPerPage={channelsPerPage}
                    paginationTotalRows={totalChannels}
                    sortIcon={<i className="fa-solid fa-chevron-down"/>}
                    onChangePage={fetchDetailedAnalytics}
                />
            )}
            <PeriodSelector selectedPeriod={selectedDetailedPeriod} onChange={handleDetailedPeriodChange}/>
            {selectedDetailedPeriod === "custom" && (
                <CustomDateRangePicker
                    customFrom={customDetailedFrom}
                    customTo={customDetailedTo}
                    setCustomFrom={setCustomDetailedFrom}
                    setCustomTo={setCustomDetailedTo}
                />
            )}
          </div>
          {hasOrganizationsPermission && (
            <div className="radius-yes p-2 bg-yes col-12">
              <h2>LLM Analytics</h2>
              {!tokenAnalytics || tokenAnalytics.length === 0 ? (
                  <p>No LLM analytics data available.</p>
              ) : (
                  <Table
                      key={selectedTokenPeriod}
                      columns={[
                        {name: "Model", selector: row => row.model_name, sortable: true},
                        {name: "Total Tokens", selector: row => row.total_tokens, sortable: true},
                        {name: "Input Tokens", selector: row => row.input_tokens, sortable: true},
                        {name: "Output Tokens", selector: row => row.output_tokens, sortable: true}
                      ]}
                      data={tokenAnalytics || []}
                      selectableRowsHighlight
                      paginationPerPage={channelsPerPage}
                      paginationTotalRows={tokenAnalytics ? tokenAnalytics.length : 0}
                      sortIcon={<i className="fa-solid fa-chevron-down"/>}
                      onChangePage={fetchTokenAnalytics}
                  />
              )}
              <PeriodSelector selectedPeriod={selectedTokenPeriod} onChange={handleTokenPeriodChange}/>
              {selectedTokenPeriod === "custom" && (
                  <CustomDateRangePicker
                      customFrom={customTokenFrom}
                      customTo={customTokenTo}
                      setCustomFrom={setCustomTokenFrom}
                      setCustomTo={setCustomTokenTo}
                  />
              )}
            </div>
          )}
        </div>

      </div>
  );
}

export default function Organization() {
  return (
    <AlertProvider>
      <OrganizationContent />
    </AlertProvider>
  );
}
